import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ClaimService, GlobalConstants, GuidService, LoggingService, SelectedFiles, ToastService, ToastType } from '@app-core';
import { EmailValidator, FieldErrorDirective } from '@app-shared';
import { Claim, ClaimUpdate, Employer } from 'app/core/models';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-success-response-employer',
    templateUrl: './success-response-employer.component.html',
    styleUrls: ['./success-response-employer.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, FieldErrorDirective],
})
export class SuccessResponseEmployerComponent implements OnInit, OnDestroy {
    private readonly claimService = inject(ClaimService);
    private readonly toastService = inject(ToastService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly guidService = inject(GuidService);
    private readonly formBuilder = inject(FormBuilder);
    private claimSubscription: Subscription;
    allowedTypes: string[] = GlobalConstants.ALLOWED_FILE_TYPES;
    maxFileUpload: number = 5;
    selectedFiles: SelectedFiles[] = [];
    claim: Claim;
    instructionText: string = GlobalConstants.SUBMIT_TEXT.EMPLOYER_INSTRUCTION;
    claimStatus = GlobalConstants.CLAIM_STATUS;
    employerForm: FormGroup;
    emailProvided: boolean = false;
    isNewClaim: boolean = false;
    isDownloading: boolean = false;
    isEmailing: boolean = false;

    currentEmailAddress: string = '';
    guid: string = '';

    constructor() {}

    ngOnInit(): void {
        this.initializeForm();
        this.claimSubscription = this.claimService.getCurrentClaim().subscribe((claim) => {
            if (claim) {
                this.guid = this.guidService.getGuid();
                this.claim = claim;
                this.employerForm.patchValue(this.claim);
                this.emailProvided = !!this.claim.parentPhysician?.emailAddress;
                this.currentEmailAddress = this.claim.parentPhysician?.emailAddress || '';
                //console.log('Email Provided', this.emailProvided);
            }
            LoggingService.log('success-response-employer.component:init ', this.claim);
        });
    }

    initializeForm() {
        this.employerForm = this.formBuilder.group({
            parentEmployer: this.formBuilder.group({
                emailAddress: ['', [Validators.required, EmailValidator.email()]],
            }),
        });
    }

    onGenerateEmployerForm() {
        this.isDownloading = true;
        this.claimSubscription = this.claimService.getEmptyEmployerForm(this.guid).subscribe({
            next: (response) => {
                try {
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const studentLastName = this.claim.accountStudent.lastName.toLocaleLowerCase();
                    const fileName = `${studentLastName}-employer-form-${this.claim.id}.pdf`;

                    // Open the PDF in a new tab
                    const newTab = window.open(url, '_blank');
                    if (newTab) {
                        newTab.onload = () => {
                            // Trigger the download
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = fileName;
                            a.click();
                            window.URL.revokeObjectURL(url);
                        };
                    } else {
                        // Fallback if the new tab could not be opened
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = fileName;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    }
                } catch (error) {
                    LoggingService.error('Error generating the PDF:', error);
                    this.toastService.show({
                        heading: 'Error',
                        message: 'There was an error generating the PDF. Please try again.',
                        toastType: ToastType.danger,
                    });
                }
                this.isDownloading = false;
            },
            error: (err) => {
                this.isDownloading = false;
                LoggingService.error('Error generating the employer form:', err);
            },
        });
    }

    /* onBackToClaimList(): void {
        this.router.navigate(['/claims'], { relativeTo: this.route });
    }

    onBackToParent(): void {
        this.router.navigate(['../parent'], { relativeTo: this.route });
    }

    onBackToClaim(): void {
        this.router.navigate(['../detail'], { relativeTo: this.route });
    }
        */

    onUploadEmployerForms() {
        //this.router.navigate(['../upload-document'], { relativeTo: this.route });
        this.router.navigate(['/upload-document-employer'], { skipLocationChange: true });
    }

    onEmailEmployer() {
        this.isEmailing = true;
        // if email has changed
        if (this.currentEmailAddress !== this.employerForm.get('parentEmployer.emailAddress').value) {
            this.markFormGroupTouched(this.employerForm);
            if (this.employerForm.valid) {
                const emailAddress = this.employerForm.get('parentEmployer.emailAddress').value;
                this.claimService.updateClaimEmployerEmail(this.claim.id, emailAddress).subscribe({
                    next: (response) => {
                        LoggingService.log('Application Updated', response);
                        this.isEmailing = false;
                        this.emailEmployer();
                    },
                    error: (error) => {
                        LoggingService.error('Error Updating Application', error);
                    },
                });
            }
        } else {
            this.emailEmployer();
        }
    }

    private emailEmployer() {
        this.isEmailing = true;
        this.claimService.emailEmployer(this.guid).subscribe({
            next: () => {
                this.toastService.show({
                    heading: 'Success',
                    message: `The employer claim form was successfully emailed to ${this.employerForm.get('parentEmployer.emailAddress').value}`,
                    toastType: ToastType.success,
                });
                this.isEmailing = false;
            },
            error: (error) => {
                this.isEmailing = false;
                LoggingService.error('Error emailing employer', error);
            },
        });
    }

    private buildClaimData(): ClaimUpdate {
        const claimUpdate = new ClaimUpdate();
        const parentEmployer = new Employer();
        parentEmployer.id = this.claim.parentEmployer?.id;
        parentEmployer.emailAddress = this.employerForm.get('parentEmployer.emailAddress').value;
        claimUpdate.parentEmployer = parentEmployer;
        return claimUpdate;
    }

    ngOnDestroy(): void {
        if (this.claimSubscription) {
            this.claimSubscription.unsubscribe();
        }
        LoggingService.log('employer-certification-component: destroyed');
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        });
    }
}
